<template>
  <div class="space-y-6">
    <div>
      <h3 class="text-lg font-semibold text-gray-900 mb-1">Resolve Ingredient Conflicts</h3>
      <p class="text-gray-600 text-sm mb-4">
        The following ingredients appear more than once. Choose which entry to keep, or merge them into a single item.
      </p>

      <div class="space-y-4">
        <div
          v-for="(group, idx) in conflicts"
          :key="idx"
          class="rounded-lg border border-amber-200 bg-amber-50 overflow-hidden"
        >
          <!-- Group header -->
          <div class="flex items-center gap-2 px-4 py-2.5 bg-amber-100 border-b border-amber-200">
            <svg class="w-4 h-4 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
            <span class="font-semibold text-amber-900 text-sm">{{ group.name }}</span>
            <span class="text-xs text-amber-700 bg-amber-200 rounded-full px-2 py-0.5">
              {{ group.items.length }} entries
            </span>
          </div>

          <!-- Resolution options -->
          <div class="p-4 space-y-2">
            <!-- Pick one option for each item -->
            <label
              v-for="(item, itemIndex) in group.items"
              :key="itemIndex"
              class="flex items-center gap-3 p-3 rounded-lg bg-white border cursor-pointer transition-all"
              :class="resolutions[idx].type === 'pick' && resolutions[idx].selectedIndex === itemIndex
                ? 'border-primary-400 ring-1 ring-primary-300'
                : 'border-gray-200 hover:border-gray-300'"
              @click="pickItem(idx, itemIndex)"
            >
              <div class="flex-shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all"
                :class="resolutions[idx].type === 'pick' && resolutions[idx].selectedIndex === itemIndex
                  ? 'border-primary-500 bg-primary-500'
                  : 'border-gray-300'">
                <div v-if="resolutions[idx].type === 'pick' && resolutions[idx].selectedIndex === itemIndex"
                  class="w-1.5 h-1.5 rounded-full bg-white"></div>
              </div>
              <div class="flex-1 min-w-0">
                <span class="text-sm font-medium text-gray-900">{{ item.name }}</span>
                <span class="ml-2 text-sm text-gray-600">{{ item.quantity }} {{ item.unit }}</span>
              </div>
              <span v-if="item.group?.name" class="text-xs text-gray-400 bg-gray-100 rounded-full px-2 py-0.5 flex-shrink-0">
                {{ item.group.name }}
              </span>
            </label>

            <!-- Merge option -->
            <label
              class="flex items-start gap-3 p-3 rounded-lg bg-white border cursor-pointer transition-all"
              :class="resolutions[idx].type === 'merge'
                ? 'border-primary-400 ring-1 ring-primary-300'
                : 'border-gray-200 hover:border-gray-300'"
              @click="setMerge(idx)"
            >
              <div class="flex-shrink-0 w-4 h-4 mt-0.5 rounded-full border-2 flex items-center justify-center transition-all"
                :class="resolutions[idx].type === 'merge'
                  ? 'border-primary-500 bg-primary-500'
                  : 'border-gray-300'">
                <div v-if="resolutions[idx].type === 'merge'"
                  class="w-1.5 h-1.5 rounded-full bg-white"></div>
              </div>
              <div class="flex-1 min-w-0">
                <span class="text-sm font-medium text-gray-900">Merge into one entry</span>
                <!-- Merge editor — visible only when selected -->
                <div v-if="resolutions[idx].type === 'merge'"
                  class="mt-3 flex items-center gap-3"
                  @click.stop>
                  <div class="flex flex-col gap-1">
                    <label class="text-xs font-medium text-gray-600">Quantity</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      v-model.number="resolutions[idx].quantity"
                      class="w-24 rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
                      placeholder="0"
                    />
                  </div>
                  <div class="flex flex-col gap-1">
                    <label class="text-xs font-medium text-gray-600">Unit</label>
                    <input
                      type="text"
                      v-model="resolutions[idx].unit"
                      class="w-32 rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
                      placeholder="e.g. grams"
                    />
                  </div>
                </div>
              </div>
            </label>
          </div>
        </div>
      </div>
    </div>

    <!-- Navigation -->
    <div class="flex justify-between mt-6">
      <button
        @click="$emit('previous')"
        class="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
      >
        Previous
      </button>
      <button
        @click="handleNext"
        :disabled="!canProceed"
        class="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Confirm &amp; Create Shopping List
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive, computed, watch } from 'vue'

export interface ConflictGroup {
  name: string
  items: any[]
}

export interface MergedItem {
  name: string
  quantity: number
  unit: string
  ingredient_id: string
  group?: any
  price: number
}

interface Resolution {
  type: 'pick' | 'merge'
  selectedIndex: number
  quantity: number
  unit: string
}

const props = defineProps<{ conflicts: ConflictGroup[] }>()

const emit = defineEmits<{
  previous: []
  next: [additionalExcluded: string[], mergedItems: MergedItem[]]
}>()

// One resolution object per conflict group
const resolutions = reactive<Resolution[]>([])

watch(
  () => props.conflicts,
  (groups) => {
    resolutions.splice(0, resolutions.length)
    for (const group of groups) {
      resolutions.push({
        type: 'pick',
        selectedIndex: 0,
        // Default merge quantity = sum of all items
        quantity: group.items.reduce((s, i) => s + (Number(i.quantity) || 0), 0),
        unit: group.items[0]?.unit ?? '',
      })
    }
  },
  { immediate: true }
)

const pickItem = (idx: number, itemIndex: number) => {
  resolutions[idx].type = 'pick'
  resolutions[idx].selectedIndex = itemIndex
}

const setMerge = (idx: number) => {
  resolutions[idx].type = 'merge'
}

const canProceed = computed(() =>
  resolutions.every((r) => {
    if (r.type === 'pick') return r.selectedIndex >= 0
    // merge: need a non-empty unit
    return r.unit.trim() !== '' && r.quantity >= 0
  })
)

const handleNext = () => {
  const additionalExcluded: string[] = []
  const mergedItems: MergedItem[] = []

  props.conflicts.forEach((group, idx) => {
    const resolution = resolutions[idx]
    // Always exclude all occurrences of conflicting ingredients from the sync,
    // then create a single shopping item with the resolved values.
    // (Items in the same conflict group may share the same ID, so we can't
    // selectively exclude by ID — exclude all and re-add the chosen one.)
    group.items.forEach(item => additionalExcluded.push(item.id))

    if (resolution.type === 'pick') {
      const selected = group.items[resolution.selectedIndex]
      mergedItems.push({
        name: group.name,
        quantity: selected.quantity,
        unit: selected.unit,
        ingredient_id: selected.ingredient_id ?? selected.id,
        group: selected.group,
        price: 0,
      })
    } else {
      // merge: use custom quantity/unit
      mergedItems.push({
        name: group.name,
        quantity: resolution.quantity,
        unit: resolution.unit,
        ingredient_id: group.items[0].ingredient_id ?? group.items[0].id,
        group: group.items[0].group,
        price: 0,
      })
    }
  })

  emit('next', additionalExcluded, mergedItems)
}
</script>
